import React, {useCallback, useEffect, useMemo} from 'react';
import ReactTooltip from 'react-tooltip';
import {
  CALCULATOR_TYPES,
  getAdditionalEnergyLabelAmount,
  getCalculatorType, numberToCurrency,
  stepStates
} from '../../services/helpers';
import { getStep4HeaderSummary } from '../../services/stepHelpers';
import { setField } from '../../store/actions/calculatorActions';
import { assetsBaseUrl } from '../../services/calculationsApi';
import {FormattedMessage, useIntl} from "react-intl";
import {confetti} from "@tsparticles/confetti";
const ToolInfoComponent = ({
                             toolData,
                             mediaMatches,
                             toggleMobileSummary,
                             mobileSummaryOpened,
                             dispatch,
                             summaryWithBanksOpened,
                             changeStepState,
                             showNotCompleted,
                           }) => {
  const intl = useIntl();
  const calculatorType = getCalculatorType();

  const {
    OwnResources,
    Woning,
    HomeEquity,
    lastActiveStepIndex,
    stepState,
    HomeEquityCosts,
    HomeMortgageAmount,
    SustainabilityUplift,
    EnergyLabel,
    response: {
      maxMortgageAmount,
      grossMonthlyCost,
      grossMonthlyCostHouse,
      netMonthlyCostHouse,
      totalNeededToBuyHouse,
      extraMoneyNeeded,
      isHouseAffordable
    }
  } = toolData;

  const getTitle = useCallback(() => {
    if (calculatorType === CALCULATOR_TYPES.kanIkDitHuisBetalen) {
      return intl.formatMessage({ id: 'summary.kanIkDitHuisBetalen.title', defaultMessage: 'Kan ik dit huis betalen?' });
    }
    if (calculatorType === CALCULATOR_TYPES.watWordenMijnMaandLasten) {
      return intl.formatMessage({ id: 'summary.watWordenMijnMaandLasten.title', defaultMessage: 'Wat worden mijn maandlasten' });
    }
    return intl.formatMessage({ id: 'summary.hoeveelKanIkLenen.title', defaultMessage: 'Hoeveel kan ik lenen?' });
  }, [calculatorType, intl]);

  const mortgagePreferenceAddition = useCallback(() => {
    const getMortgagePreference = () => {
      return (getCalculatorType() !== CALCULATOR_TYPES.watWordenMijnMaandLasten)
      ? intl.formatMessage({
        id: 'summary.titleMonthlyCost.mortgagePreference.maximum',
        defaultMessage: 'maximale'
      })
      : intl.formatMessage({
        id: 'summary.titleMonthlyCost.mortgagePreference.preferred',
        defaultMessage: 'gewenste'
      });
    }

    return Woning > 0
    ? <FormattedMessage
    id='summary.titleMonthlyCost.addition'
    defaultMessage='o.b.v. {mortgagePreference} hypotheek'
    values={{ mortgagePreference: getMortgagePreference()}}
    />
    : '';
  }, [Woning, intl]);

  useEffect(() => {
    if (getCalculatorType() === CALCULATOR_TYPES.kanIkDitHuisBetalen && isHouseAffordable !== false) {

      var defaults = {
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      };

      confetti({
        ...defaults,
        shapes: ["image"],
        shapeOptions: {
          image: {
            src: `${assetsBaseUrl}/assets/svg/triangle.svg`,
            replaceColor: true,
            width: 32,
            height: 40,
          },
        },
        colors: ["#0F916C", "#00325D", "#59b2fc", "#ce182d", "#F5B220"],
      });
    }
  }, [isHouseAffordable]);

  const data = useMemo(() => {
    let fields = [];
    const ownMoney = getStep4HeaderSummary(HomeEquity, OwnResources, HomeEquityCosts, HomeMortgageAmount, Woning, true, intl);
    const title = getTitle();
    const showMaxMortgage = stepState[0] === "filled" && stepState[1] === "filled";
    const sustainabilityBudget = getAdditionalEnergyLabelAmount(EnergyLabel?.value, SustainabilityUplift === 1);

    const titleGrossMonthlyCost= <FormattedMessage id="summary.titleMonthlyCost.gross" defaultMessage="Indicatie bruto maandlasten {addition}" values={{ addition: mortgagePreferenceAddition()}} />
    const titleNetMonthlyCost = <FormattedMessage id="summary.titleMonthlyCost.net" defaultMessage="Indicatie netto maandlasten {addition}" values={{ addition: mortgagePreferenceAddition()}} />

    const ownMoneyInformation = intl.formatMessage({ id:'summary.ownMoneyInformation', defaultMessage : 'Dit is het bedrag dat je hebt aangegeven in te willen brengen bij de aankoop van je nieuwe woning.'});
    const extraMoneyNeededInformation = intl.formatMessage({ id:'summary.extraMoneyNeededInformation', defaultMessage : 'Dit is het extra bedrag aan eigen geld dat je nodig hebt om deze woning te kunnen betalen. Met dit bedrag worden o.a. de kosten voor de notaris, hypotheekakte, taxatie en hypotheekadvies betaald. Eventuele kosten om te overbieden zitten hier niet bij inbegrepen.'});
    const sustainabilityUpliftInformation = intl.formatMessage({ id:'summary.sustainabilityUpliftInformation', defaultMessage : 'Als je de woning die je op het oog hebt wilt verduurzamen, kan je mogelijk aanspraak maken op een verduurzamingsbudget bovenop je maximale leenbedrag. Let op: dit bedrag mag alleen besteed worden aan energiebesparende voorzieningen in en aan de woning en de uiteindelijke hoogte van het additionele verduurzamingsbudget hangt af van de taxatiewaarde van de woning.'});
    const sustainabilityUpliftInformationNoLabel = intl.formatMessage({ id:'summary.sustainabilityUpliftInformationNoLabel', defaultMessage : 'Als je de woning die je op het oog hebt wilt verduurzamen, kan je mogelijk aanspraak maken op een verduurzamingsbudget bovenop je maximale leenbedrag. Let op: dit bedrag mag alleen besteed worden aan energiebesparende voorzieningen in en aan de woning en de uiteindelijke hoogte van het additionele verduurzamingsbudget hangt af van de taxatiewaarde van de woning. Je geeft aan dat er je woning (nog) geen energielabel heeft. Bij de verkoop van een woning moet een energielabel overhandigd worden. Het daadwerkelijke energielabel kan van invloed zijn op de hoogte van het verduurzamingsbudget.'});
    const maxMortgageInformationNoHouse = intl.formatMessage({ id:'summary.maxMortgageInformationNoHouse', defaultMessage : 'Je geeft aan dat je nog geen woning op het oog hebt. Om je maximale hypotheekbedrag te berekenen zijn we uitgegaan van het laagste energielabel. Afhankelijk van het energielabel kan je maximale hypotheek € 5000-€ 50.000 hoger zijn dan het bedrag dat hier getoond wordt.'});
    const maxMortgageInformationNoLabel = intl.formatMessage({ id:'summary.maxMortgageInformationNoLabel', defaultMessage : 'Je geeft aan dat de woning die je op het oog hebt (nog) geen energielabel heeft. Bij de verkoop van een woning moet een energielabel overhandigd worden. Afhankelijk van het energielabel kan je maximale hypotheek € 5000-€ 50.000 hoger zijn dan het bedrag dat hier getoond wordt.'});


    if (calculatorType === CALCULATOR_TYPES.hoeveelKanIkLenen) {
      fields = [
        {
          title: <FormattedMessage id={'summary.list.maxMortgageAmount.title'} defaultMessage={'Jouw maximale hypotheek'} />,
          value: maxMortgageAmount,
          toolTip: (Woning > 0) ? EnergyLabel?.value === "-" ? maxMortgageInformationNoLabel: false : maxMortgageInformationNoHouse
        },
        {
          title: titleGrossMonthlyCost,
          value: grossMonthlyCost,
        },
        {
          title: <FormattedMessage id={'summary.list.sustainabilityBudget.title'} defaultMessage={'Verduurzamingsbudget'} />,
          value: sustainabilityBudget,
          hide: SustainabilityUplift === 0 || sustainabilityBudget === 0 || Woning === 0,
          toolTip: EnergyLabel?.value === "-" ? sustainabilityUpliftInformationNoLabel : sustainabilityUpliftInformation
        },
        {
          title: <FormattedMessage id={'summary.list.housePrice.title'} defaultMessage={'Vraagprijs woning'} />,
          value: Woning,
          hide: Woning === 0,
        },
        {
          title:  <FormattedMessage id={'summary.list.titleMonthlyCostHouse.gross'} defaultMessage={'Indicatie bruto maandlasten o.b.v. woning'} />,
          value: grossMonthlyCostHouse,
          hide: grossMonthlyCostHouse === 0 || Woning === 0
        },
        {
          title: <FormattedMessage id={'summary.list.ownMoney.title'} defaultMessage={'Inleg eigen geld'} />,
          value: ownMoney,
          hide: ownMoney === 0 || Woning === 0,
          toolTip: ownMoneyInformation
        },

      ];
    }


    if (calculatorType === CALCULATOR_TYPES.kanIkDitHuisBetalen) {
      fields = [
        {
          title: <FormattedMessage id={'summary.list.totalNeededToBuyHouse.title'} defaultMessage={'Totaal nodig voor je huis'} />,
          value: totalNeededToBuyHouse,
          addResultRedClass: isHouseAffordable === false,
          hide: typeof totalNeededToBuyHouse === 'undefined'
        },
        {
          title: <FormattedMessage id={'summary.list.maxMortgageAmount.title'} defaultMessage={'Jouw maximale hypotheek'} />,
          value: maxMortgageAmount,
          iamNeverTheFirstItem: true,
          toolTip: (Woning > 0) ? EnergyLabel?.value === "-" ? maxMortgageInformationNoLabel: false : maxMortgageInformationNoHouse
        },
        {
          title: <FormattedMessage id={'summary.list.sustainabilityBudget.title'} defaultMessage={'Verduurzamingsbudget'} />,
          value: sustainabilityBudget,
          hide: SustainabilityUplift === 0 || sustainabilityBudget === 0 || Woning === 0,
          toolTip: EnergyLabel?.value === "-" ? sustainabilityUpliftInformationNoLabel : sustainabilityUpliftInformation
        },
        {
          title: titleGrossMonthlyCost,
          value: grossMonthlyCost
        },
        {
          title: <FormattedMessage id={'summary.list.housePrice.title'} defaultMessage={'Vraagprijs woning'} />,
          value: Woning,
          hide: Woning === 0,
        },
        {
          title:  <FormattedMessage id={'summary.list.titleMonthlyCostHouse.gross'} defaultMessage={'Indicatie bruto maandlasten o.b.v. woning'} />,
          value: grossMonthlyCostHouse,
          hide: grossMonthlyCostHouse === 0 || Woning === 0
        },
        {
          title: <FormattedMessage id={'summary.list.extraMoneyNeeded.title'} defaultMessage={'Extra in te leggen'} />,
          value: extraMoneyNeeded,
          hide: extraMoneyNeeded === 0,
          toolTip: extraMoneyNeededInformation
        },
        {
          title: <FormattedMessage id={'summary.list.ownMoney.title'} defaultMessage={'Inleg eigen geld'} />,
          value: ownMoney,
          hide: ownMoney === 0 || Woning === 0,
          toolTip: ownMoneyInformation
        }
      ];

    }

    if (calculatorType === CALCULATOR_TYPES.watWordenMijnMaandLasten) {
      fields = [
        {
          title: titleGrossMonthlyCost,
          value: grossMonthlyCostHouse,
        },
        {
          title: titleNetMonthlyCost,
          value: netMonthlyCostHouse,
        },
        {
          title: <FormattedMessage id={'summary.list.preferredMortgageAmount.title'} defaultMessage={'Gewenst hypotheekbedrag'} />,
          value: Woning,
          hide: Woning === 0,
        },
        {
          title: <FormattedMessage id={'summary.list.ownMoney.title'} defaultMessage={'Inleg eigen geld'} />,
          value: ownMoney,
          hide: ownMoney === 0 || Woning === 0,
          toolTip: ownMoneyInformation,
        },
        {
          title: <FormattedMessage id={'summary.list.maxMortgageAmount.title'} defaultMessage={'Jouw maximale hypotheek'} />,
          value: maxMortgageAmount,
          hide: maxMortgageAmount === 0 || !showMaxMortgage,
          toolTip: maxMortgageInformationNoHouse
        },
      ];
    }

    // Filter out hidden fields and return the result
    return { fields: fields.filter(field => !field.hide), title};

  }, [stepState, Woning, EnergyLabel, SustainabilityUplift, maxMortgageAmount, grossMonthlyCost, grossMonthlyCostHouse, totalNeededToBuyHouse, extraMoneyNeeded, isHouseAffordable, HomeEquity, HomeEquityCosts, HomeMortgageAmount, OwnResources, calculatorType, getTitle, intl, mortgagePreferenceAddition, netMonthlyCostHouse]);

  const toggleSummary = () => {
    if (typeof summaryWithBanksOpened !== "boolean") {
      toggleMobileSummary();
    } else {
      dispatch(setField("summaryWithBanksOpened", !summaryWithBanksOpened));
      changeStepState(lastActiveStepIndex, stepStates.filled);
    }
  };

  return (
  <>
    <div className="ct-step__summary-title">
      {data.title}
    </div>
    {showNotCompleted && (
    <div className="ct-step__summary-text">
      <FormattedMessage id={'summary.notCompleted.text'} defaultMessage={'Dit resultaat is gebaseerd op je leeftijd en inkomen. Maak de berekening af voor een compleet overzicht van hoeveel je kunt lenen.'} />
    </div>
    )}

    {getCalculatorType() === CALCULATOR_TYPES.kanIkDitHuisBetalen &&
    totalNeededToBuyHouse > 0 ? (
    <div
    className={`ct-step__summary-answer ${isHouseAffordable === false ? 'ct-step__summary-answer__red' : 'ct-step__summary-answer__green'}`}>

      <div className={`ct-step__summary-answer__wrapper`}>
        <img src={`${assetsBaseUrl}/assets/svg/${isHouseAffordable === false ? 'round-circle-close.svg' : 'round-circle-check.svg'}`} alt=""/>
        <div className={`ct-step__summary-answer__wrapper__text`}>
        <FormattedMessage
          id="summary.isHouseAffordable"
          defaultMessage="Het lijkt erop dat je het huis {isHouseAffordable} kunt betalen"
          values={{
            isHouseAffordable: isHouseAffordable === false ?
            intl.formatMessage({id: 'summary.isHouseAffordable.no', defaultMessage: 'niet'}) :
            ''
          }}
          />
        </div>
      </div>
      {isHouseAffordable !== false && (
      <img className={`ct-step__summary-answer__triangles`} src={`${assetsBaseUrl}/assets/images/triangles-confirmation.png`} alt="success" />
      )}

      <div id={"tsparticles"}></div>
    </div>
    ) : null}

    <div className="ct-step__summary-results">
      {data.fields && data.fields.map((item, i) => (
      <div key={i} className={`${(i === 0 && !item.iamNeverTheFirstItem) && `ct-step__summary-results-maximale ct-step__summary-results-maximale__${getCalculatorType()}`}`}>
        <div className='ct-step__summary-results__key'>
          <span>{item.title}</span>
          {item.toolTip && (
          <span>
                <img className="ct-step__tool-tip ct-step__tool-tip--summary"
                     data-tip={item.toolTip}
                     src={`${assetsBaseUrl}/assets/svg/info.svg`} alt=""/>
                <ReactTooltip place={"top"} effect={"solid"} html={true} disabled={true}/>
              </span>
          )}
        </div>
        <div
        className={`ct-step__summary-results__value ct-step__summary-results__value--${calculatorType} ${item.addResultRedClass && 'ct-step__summary-results-red'}`}>
          {numberToCurrency(item.value ? item.value : 0)}
        </div>
        {i === 0 && mediaMatches && summaryWithBanksOpened !== true && (
        <img
        src={`${assetsBaseUrl}/assets/svg/arrow-down-secondary.svg`}
        className={`${mobileSummaryOpened && "ct-step__summary-mobile-arrow-rotated"}`}
        onClick={toggleSummary}
        alt=""
        />
        )}
      </div>
      ))}
    </div>
  </>
  );
};

export default ToolInfoComponent;
